import React, {useEffect, useState} from 'react';
import {PredefinedOptionJson} from "../../api/json/PredefinedOptionJson";
import {Stack, TextField, Typography} from "@mui/material";
import ColumnPredefinedOptionsEditorRow from "./ColumnPredefinedOptionsEditorRow";
import {ColumnRequests} from "../../api/requests/ColumnRequests";
import {ProjectJson} from "../../api/json/ProjectJson";
import {ColumnPredefinedOptionsJson} from "../../api/json/ColumnJson";

interface ColumnPredefinedOptionsEditorProps {
    project: ProjectJson
    fieldId?: string
    options: PredefinedOptionJson[]
    updateOptions: (options: PredefinedOptionJson[], saveChanges: boolean) => void
}

function ColumnPredefinedOptionsEditor({project, fieldId, options, updateOptions}: ColumnPredefinedOptionsEditorProps) {
    const [newOption, setNewOption] = useState("")
    const [valueAlreadyExists, setValueAlreadyExists] = useState(false)

    useEffect(() => {
        const listener = (event: KeyboardEvent) => {
            if (event.key === "Enter") {
                addNewOption()
            }
        }
        window.addEventListener('keyup', listener);
        return () => window.removeEventListener('keyup', listener)
    }, [newOption])

    const addNewOption = () => {
        if (newOption.length === 0)
            return

        if (valueAlreadyExists)
            return

        setNewOption("")
        addOption(newOption)
    }

    useEffect(() => {
        setValueAlreadyExists(newOption.length > 0 && options.find(option => option.value === newOption) !== undefined)
    }, [newOption])

    useEffect(() => {
        if (!fieldId) return

        // Get row counts and update the options
        ColumnRequests.get(project.id, fieldId)
            .then(column => {
                const optionsWithExampleCounts = (column as ColumnPredefinedOptionsJson).predefinedOptions
                if (optionsWithExampleCounts)
                    updateOptions(optionsWithExampleCounts, false)
            })
    }, [fieldId])

    const addOption = (newValue: string) => {
        const newOptions = [...options]
        const newOption = {
            value: newValue,
            numberOfRows: 0,
        }
        newOptions.push(newOption)
        updateOptions(newOptions, true)
    }

    const handleUpdate = (newValue: string, index: number) => {
        const newOptions = [...options]
        newOptions[index].value = newValue
        updateOptions(newOptions, false)
    }

    const handleColorUpdate = (color: string | undefined, index: number) => {
        const newOptions = [...options]
        newOptions[index] = {...newOptions[index], color}
        updateOptions(newOptions, true)
    }

    const handleRemoveDeleted = (option: PredefinedOptionJson, index: number) => {
        const updated = [...options]
        updated.splice(index, 1)
        return updateOptions(updated, true)
    }

    return (
        <>
            <Stack spacing={0.5}>
                <Typography variant="subtitle2">Predefined Options:</Typography>
                {options?.map((option, index) =>
                    <ColumnPredefinedOptionsEditorRow
                        key={index}
                        project={project}
                        fieldId={fieldId}
                        option={option}
                        didDelete={() => handleRemoveDeleted(option, index)}
                        didUpdate={(newValue) => handleUpdate(newValue, index)}
                        didUpdateColor={(color) => handleColorUpdate(color, index)}
                    />
                )}
                <TextField
                    value={newOption}
                    helperText={valueAlreadyExists ? "This value is already in the list." : "Type and press enter to add new option."} size="small"
                    error={valueAlreadyExists}
                    onChange={(e) => setNewOption(e.target.value)}
                    onBlur={addNewOption}
                />
            </Stack>
        </>
    );
}

export default ColumnPredefinedOptionsEditor;